import React from 'react';
import { LucideIcon, ArrowUpRight, ArrowDownRight, Sparkles, Lightbulb } from 'lucide-react';
import { clsx } from 'clsx';
import { HoverTip } from './HoverTip';

interface StatsCardProps {
    label: string;
    value: string | number;
    icon: LucideIcon;
    trend?: number;
    trendLabel?: string;
    sublabel?: string;
    progress?: number;
    color?: 'indigo' | 'emerald' | 'amber' | 'rose';
    theme?: 'light' | 'dark';
    /** When set, an info affordance shows this message as a hover overlay (used for the "why this matters" nudge on negative insights). */
    tooltip?: string;
    /** Status strip at the card bottom: 'nudge' (amber, do-this hint) or 'positive' (emerald, reward). */
    banner?: { tone: 'positive' | 'nudge'; text: string };
    /** 'cap' (default): full bar = limit reached (rose warning). 'goal': full bar = goal met (stays the tile color). */
    progressTone?: 'cap' | 'goal';
}

const colorIconMap = {
    indigo: 'from-indigo-500/20 to-indigo-600/5 text-indigo-400 border-indigo-500/20 bg-indigo-500',
    emerald: 'from-emerald-500/20 to-emerald-600/5 text-emerald-400 border-emerald-500/20 bg-emerald-500',
    amber: 'from-amber-500/20 to-amber-600/5 text-amber-400 border-amber-500/20 bg-amber-500',
    rose: 'from-rose-500/20 to-rose-600/5 text-rose-400 border-rose-500/20 bg-rose-500',
};

export const StatsCard: React.FC<StatsCardProps> = ({
    label,
    value,
    icon: Icon,
    trend,
    trendLabel,
    sublabel,
    progress,
    color = 'indigo',
    theme = 'dark',
    tooltip,
    banner,
    progressTone = 'cap'
}) => {
    const isPositive = trend > 0;
    const isLight = theme !== 'dark';
    const progressPct = progress !== undefined ? Math.round(progress * 100) : undefined;
    const progressColor = progress === undefined
        ? null
        : progressTone === 'goal'
            ? colorIconMap[color].split(' ')[4]
            : progress >= 1
                ? 'bg-rose-500'
                : progress >= 0.85
                    ? 'bg-amber-500'
                    : colorIconMap[color].split(' ')[4];

    return (
        <div className={clsx(
            "p-6 rounded-2xl relative overflow-hidden group transition-all duration-300",
            isLight
                ? "bg-white border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200"
                : "glass-panel border border-white/5 hover:border-white/10"
        )}>
            <div className={clsx(
                "absolute top-0 right-0 w-32 h-32 opacity-10 blur-3xl rounded-full translate-x-10 -translate-y-10 group-hover:opacity-20 transition-opacity",
                colorIconMap[color].split(' ')[4]
            )} />

            <div className="flex justify-between items-start mb-4">
                <div className={clsx("p-3 rounded-xl border", colorIconMap[color].split(' ').slice(0, 4).join(' '))}>
                    <Icon size={24} />
                </div>
                {trend !== undefined && (
                    <div className={clsx(
                        "flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg",
                        isPositive ? "bg-emerald-500/10 text-emerald-500" : "bg-rose-500/10 text-rose-400"
                    )}>
                        {isPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                        {Math.abs(trend)}%
                    </div>
                )}
            </div>

            <div className="space-y-1">
                <h3 className={clsx("text-sm font-medium", isLight ? "text-slate-400" : "text-slate-400")}>{label}</h3>
                <p className={clsx("text-3xl font-bold tracking-tight", isLight ? "text-slate-900" : "text-white")}>{value}</p>
            </div>

            {progressPct !== undefined && (
                <div className="mt-4 space-y-1.5">
                    <div className={clsx(
                        "h-1.5 rounded-full overflow-hidden",
                        isLight ? "bg-slate-100" : "bg-white/5"
                    )}>
                        <div
                            className={clsx("h-full rounded-full transition-all", progressColor)}
                            style={{ width: `${Math.min(progressPct, 100)}%` }}
                        />
                    </div>
                    {sublabel && (
                        <p className={clsx("text-xs", isLight ? "text-slate-500" : "text-slate-400")}>{sublabel}</p>
                    )}
                </div>
            )}

            {progressPct === undefined && sublabel && (
                <p className={clsx("text-xs mt-3", isLight ? "text-slate-500" : "text-slate-400")}>{sublabel}</p>
            )}

            {banner && (
                <div className={clsx(
                    "mt-4 flex items-start gap-1.5 px-2.5 py-2 rounded-lg text-[11px] font-medium leading-snug",
                    banner.tone === 'positive'
                        ? (isLight ? "bg-emerald-50 text-emerald-700" : "bg-emerald-500/10 text-emerald-300")
                        : (isLight ? "bg-amber-50 text-amber-700" : "bg-amber-500/10 text-amber-300")
                )}>
                    {banner.tone === 'positive'
                        ? <Sparkles size={13} className="shrink-0 mt-0.5" />
                        : <Lightbulb size={13} className="shrink-0 mt-0.5" />}
                    <span>{banner.text}</span>
                </div>
            )}

            {trendLabel && trend !== undefined && (
                <p className={clsx("text-xs mt-4 flex items-center gap-1", isLight ? "text-slate-400" : "text-slate-500")}>
                    <span className={clsx("font-semibold", isPositive ? "text-emerald-500/70" : "text-rose-500/70")}>
                        {isPositive ? '+' : '-'} {trendLabel}
                    </span> vs mes pasado
                </p>
            )}

            {tooltip && <HoverTip tip={tooltip} theme={theme} />}
        </div>
    );
};
